import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';

const WHATSAPP_JOIN =
  'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Quiero%20que%20mi%20negocio%20d%C3%A9%20el%20salto%20con%20su%20sistema.';

// Public asset URL respecting Vite base path (logos live in /public/clients).
const asset = (file: string) => `${import.meta.env.BASE_URL}clients/${file}`;

interface ClientCase {
  name: string;
  monogram: string;
  industry: string;
  logo?: string; // filename inside /public/clients — optional; falls back to monogram
  tileBg?: string; // custom logo-tile background (for logos designed on dark backgrounds)
}

// ─── Client roster (ordered by industry) ──────────────────────────────────────
// To add a client: drop the logo in /public/clients and add an entry here.
const CLIENTS: ClientCase[] = [
  // Dealers
  { name: 'Liwama Auto Import', monogram: 'LW', industry: 'Dealers', logo: 'liwama.png' },
  { name: 'BP Motors Santo Domingo', monogram: 'BP', industry: 'Dealers', logo: 'bp-motors.png' },
  // Restaurantes
  { name: 'Depacheco Mariscos', monogram: 'DP', industry: 'Restaurantes', logo: 'depacheco.png' },
  { name: 'Smoke Hill', monogram: 'SH', industry: 'Restaurantes', logo: 'smokehill.png', tileBg: '#111722' },
  { name: 'Rajanoris Restaurant', monogram: 'RJ', industry: 'Restaurantes', logo: 'rajanoris.jpg', tileBg: '#141821' },
  { name: 'Plaza La Cotorrita PLC', monogram: 'PC', industry: 'Restaurantes', logo: 'cotorrita.png' },
  // Distribución
  { name: 'Grupo Genca', monogram: 'GC', industry: 'Distribución', logo: 'genca.png' },
  { name: 'DM Suministros Mejía SRL', monogram: 'DM', industry: 'Distribución', logo: 'dm-suministros.svg' },
  { name: 'Soluciones Glomix AM SRL', monogram: 'SG', industry: 'Distribución', logo: 'glomix.svg' },
  // Ferreterías
  { name: 'Agroferretería Quimo', monogram: 'AQ', industry: 'Ferreterías', logo: 'quimo.png' },
  // Tecnología
  { name: 'ChristopherCell', monogram: 'CC', industry: 'Tecnología', logo: 'christophercell.svg' },
  // Retail / Comercios
  { name: "Henry's Collection", monogram: 'HC', industry: 'Retail', logo: 'henrys.jpg', tileBg: '#000000' },
  // Alimentos
  { name: 'Embutidos Doña Negra', monogram: 'DN', industry: 'Alimentos', logo: 'dona-negra.png' },
];

// ─── Single logo chip ─────────────────────────────────────────────────────────
function LogoChip({ client, isLight }: { client: ClientCase; isLight: boolean }) {
  const tileBg = client.tileBg || (isLight ? '#ffffff' : '#f4f7fc');
  return (
    <div className="flex flex-col items-center gap-2.5 flex-shrink-0 w-[168px] md:w-[190px]">
      <div
        className="w-full h-24 md:h-28 rounded-2xl flex items-center justify-center p-4 overflow-hidden transition-transform duration-300 hover:scale-[1.04]"
        style={{
          backgroundColor: tileBg,
          border: isLight ? '1px solid rgba(10,22,40,0.08)' : '1px solid rgba(255,255,255,0.08)',
          boxShadow: isLight
            ? '0 4px 16px rgba(10,22,40,0.06)'
            : '0 6px 20px rgba(0,0,0,0.35)',
        }}
      >
        {client.logo ? (
          <img
            src={asset(client.logo)}
            alt={`${client.name} logo`}
            loading="lazy"
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <span className="font-heading text-2xl font-extrabold text-navy-500">{client.monogram}</span>
        )}
      </div>
      <span
        className="font-body text-xs font-semibold text-center leading-tight"
        style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.60)' }}
      >
        {client.name}
      </span>
    </div>
  );
}

// ─── One marquee row ──────────────────────────────────────────────────────────
function MarqueeRow({
  clients,
  isLight,
  duration,
  reverse,
}: {
  clients: ClientCase[];
  isLight: boolean;
  duration: number;
  reverse?: boolean;
}) {
  // Repeat the row so a single "half" is wide enough, then render the half twice
  // for a seamless -50% loop.
  const half = [...clients, ...clients];
  const track = [...half, ...half];
  return (
    <div className="cases-marquee py-2">
      <div
        className={`cases-track ${reverse ? 'rev' : ''}`}
        style={{ ['--dur' as string]: `${duration}s` }}
      >
        {track.map((c, i) => (
          <LogoChip key={`${c.name}-${i}`} client={c} isLight={isLight} />
        ))}
      </div>
    </div>
  );
}

export default function Testimonials() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [visible, setVisible] = useState(false);
  const sectionRef = (el: HTMLElement | null) => {
    if (el && !visible) {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        },
        { threshold: 0.05 },
      );
      obs.observe(el);
    }
  };

  const mid = Math.ceil(CLIENTS.length / 2);
  const rowA = CLIENTS.slice(0, mid);
  const rowB = CLIENTS.slice(mid);

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className={`py-14 md:py-24 px-5 md:px-6 ${isLight ? '' : 'bg-navy-900'}`}
      style={{ backgroundColor: isLight ? '#f8fbff' : undefined }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className="text-center mb-9 md:mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">
            {t('test_label')}
          </span>
          <h2
            className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4"
            style={{ color: isLight ? '#0a1628' : '#ffffff' }}
          >
            {t('test_title_1')} <span className="text-electric">{t('test_title_accent')}</span>
          </h2>
          <p
            className="font-body text-sm md:text-base max-w-2xl mx-auto"
            style={{ color: isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.60)' }}
          >
            {t('test_sub')}
          </p>
        </div>

        {/* Marquee rows */}
        <div
          className="flex flex-col gap-4 md:gap-6"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.2s',
          }}
        >
          <MarqueeRow clients={rowA} isLight={isLight} duration={46} />
          <MarqueeRow clients={rowB} isLight={isLight} duration={40} reverse />
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3 mt-10 md:mt-14 text-center">
          <p
            className="font-heading text-base md:text-lg font-bold"
            style={{ color: isLight ? '#0a1628' : '#ffffff' }}
          >
            {t('cases_join_title')}
          </p>
          <a
            href={WHATSAPP_JOIN}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-2 font-bold text-sm py-3 px-6 rounded-xl font-body transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-[1.03] active:scale-[0.97]"
            style={{ background: 'linear-gradient(135deg, #00cfff, #0ea5e9)', color: '#04121f' }}
          >
            <i className="ri-whatsapp-line text-base"></i>
            {t('cases_join_cta')}
          </a>
        </div>
      </div>
    </section>
  );
}
